import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, User, Home, Phone, Tag, HardDrive, ShieldAlert, MessageSquare, Paperclip, X, Maximize2, Calendar, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from '../components/sidebar.js'; 

const TicketDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    
    const user = JSON.parse(localStorage.getItem('user')) || { name: "User", role: "Staff" };

    useEffect(() => {
        const fetchTicket = async () => {
            try {
                const res = await axios.get(`http://localhost:5000/api/tickets/${id}`);
                setTicket(res.data);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching ticket", err);
                setLoading(false);
            }
        };
        fetchTicket();
    }, [id]);

    const handleLogout = () => {
        localStorage.removeItem('user');
        navigate('/');
    };

    if (loading) return <div className="loading-state">Loading Ticket Details...</div>;
    if (!ticket) return <div className="error-state">Ticket not found.</div>;

    const attachmentUrl = `http://localhost:5000${ticket.attachment_url}`;

    return (
        <div className="app-container">
            <Sidebar 
                user={user} 
                handleLogout={handleLogout} 
                activeTab="tickets" 
                setActiveTab={() => navigate('/dashboard')} 
            />

            <main className="main-content">
                <AnimatePresence>
                    {isPreviewOpen && (
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsPreviewOpen(false)}
                            style={{
                                position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                                backgroundColor: 'rgba(0, 0, 0, 0.9)', zIndex: 9999,
                                display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'zoom-out'
                            }}
                        >
                            <button 
                                onClick={(e) => { e.stopPropagation(); setIsPreviewOpen(false); }}
                                style={{
                                    position: 'absolute', top: '20px', right: '20px', background: 'white',
                                    border: 'none', borderRadius: '50%', padding: '10px', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                                }}
                            >
                                <X size={24} color="black" />
                            </button>
                            <motion.img 
                                initial={{ scale: 0.8 }} animate={{ scale: 1 }} exit={{ scale: 0.8 }}
                                src={attachmentUrl} alt="Fullscreen Preview"
                                style={{ maxWidth: '90%', maxHeight: '90%', borderRadius: '8px' }}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>

                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ padding: '30px' }}>
                    <button 
                        onClick={() => navigate('/dashboard')} 
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', border: 'none', background: 'none', color: '#6366f1', fontWeight: '600', cursor: 'pointer', marginBottom: '24px' }}
                    >
                        <ArrowLeft size={20} /> Back to Dashboard
                    </button>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '30px',marginBottom:`30px` }}>
                        
                        {/* LEFT SIDE: TICKET DETAILS */}
                        <div style={{ background: '#fff', padding: '30px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)' }}>
                            <div style={{ borderBottom: '1px solid #f1f5f9', marginBottom: '24px', paddingBottom: '16px' }}>
                                <span style={{ color: '#6366f1', fontWeight: '700', fontSize: '14px' }}>TICKET #{ticket.id}</span>
                                <h1 style={{ fontSize: '28px', color: '#1e293b', marginTop: '8px' }}>{ticket.subject}</h1>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
                                <div>
                                    <h4 className="detail-section-label">Property Information</h4>
                                    <InfoItem icon={<Home size={18}/>} label="Property Name" value={ticket.property_name} />
                                    <InfoItem icon={<User size={18}/>} label="Contact Name" value={ticket.contact_name} />
                                    <InfoItem icon={<User size={18}/>} label="Tenant" value={ticket.property_tenant} />
                                    <InfoItem icon={<Phone size={18}/>} label="Property Phone" value={ticket.property_phone} />
                                </div>

                                <div>
                                    <h4 className="detail-section-label">System Information</h4>
                                    <InfoItem icon={<Tag size={18}/>} label="Property Type" value={ticket.property_type} />
                                    <InfoItem icon={<HardDrive size={18}/>} label="Server Type" value={ticket.server_type} />
                                    <InfoItem icon={<ShieldAlert size={18}/>} label="Priority" value={ticket.priority} highlightPriority={ticket.priority?.toLowerCase()} />
                                    <InfoItem icon={<MessageSquare size={18}/>} label="Status" value={ticket.status} highlightStatus={ticket.status?.toLowerCase().replace(/\s+/g, '-')} />
                                </div>
                            </div>

                            {/* <div style={{ marginTop: '30px', paddingTop: '24px', borderTop: '1px solid #f1f5f9' }}>
                                <h4 className="detail-section-label">Initial Description</h4>
                                <p style={{ color: '#475569', lineHeight: '1.7', fontSize: '15px', whiteSpace: 'pre-wrap' }}>
                                    {ticket.description || "No description provided."}
                                </p>
                            </div> */}

                            {/* NEW: STAFF ACTION LOG / TIMELINE (History) */}
                            {/* ACTIVITY & STAFF UPDATES */}
                            <div style={{ marginTop: '30px', paddingTop: '24px', borderTop: '2px solid #f1f5f9' }}>
                                <h4 className="detail-section-label" style={{ color: '#6366f1', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Clock size={16} /> Activity & Staff Updates
                                </h4>
                                <div className="timeline-container" style={{ marginTop: '20px' }}>
                                    
                                    {/* 1. INITIAL TICKET CREATION - Highlighted Version */}
                                    <div className="timeline-item">
                                        <div className="timeline-dot-creator"></div>
                                        <div className="timeline-content creator-highlight-bg">
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                <div>
                                                    <span className="creator-badge">Ticket Created</span>
                                                    <strong style={{ marginLeft: '10px', color: '#1e293b' }}>{ticket.ticket_owner}</strong>
                                                </div>
                                                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>{ticket.date}</span>
                                            </div>
                                            <div style={{ padding: '12px', background: '#ffffff', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                                                <p style={{ margin: 0, fontSize: '14px', color: '#334155', lineHeight: '1.6' }}>
                                                    {ticket.description || "No description provided."}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 2. STAFF UPDATES - Chronological Queue */}
                                    {ticket.updates && [...ticket.updates]
                                        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                                        .map((update, index) => (
                                            <div className="timeline-item" key={index}>
                                                <div className="timeline-dot-staff"></div>
                                                <div className="timeline-content staff-bg">
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                        <strong style={{ color: '#4f46e5', fontSize: '14px' }}>{update.staff_name}</strong>
                                                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                            {new Date(update.timestamp).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                                                        </span>
                                                    </div>
                                                    <p style={{ margin: 0, fontSize: '14px', color: '#1e293b', lineHeight: '1.5' }}>
                                                        {update.note}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>

                        {/* RIGHT SIDE: ATTACHMENT */}
                        <div>
                            <h3 style={{ fontSize: '16px', display: 'flex', alignItems: 'center', gap: '10px', color: '#475569', marginBottom: '15px' }}>
                                <Paperclip size={18} /> Attachment Preview
                            </h3>
                            
                            <div style={{ 
                                border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', 
                                background: '#f8fafc', minHeight: '500px', display: 'flex', 
                                justifyContent: 'center', alignItems: 'center', position: 'relative',
                                cursor: ticket.attachment_url && !ticket.attachment_url.toLowerCase().endsWith('.pdf') ? 'zoom-in' : 'default'
                            }}>
                                {ticket.attachment_url ? (
                                    ticket.attachment_url.toLowerCase().endsWith('.pdf') ? (
                                        <iframe src={attachmentUrl} title="Attachment Preview" style={{ width: '100%', height: '650px', border: 'none' }} />
                                    ) : (
                                        <div onClick={() => setIsPreviewOpen(true)} style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            <img src={attachmentUrl} alt="Ticket Attachment" style={{ maxWidth: '100%', maxHeight: '650px', objectFit: 'contain' }} />
                                            <div style={{ position: 'absolute', bottom: '15px', right: '15px', background: 'rgba(255,255,255,0.8)', padding: '5px', borderRadius: '6px' }}>
                                                <Maximize2 size={16} color="#6366f1" />
                                            </div>
                                        </div>
                                    )
                                ) : (
                                    <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>
                                        <Paperclip size={48} style={{ marginBottom: '10px', opacity: 0.3 }} />
                                        <p>No attachment for this ticket</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </motion.div>
            </main>

            <style>{`
                .detail-section-label {
                    text-transform: uppercase;
                    font-size: 11px;
                    letter-spacing: 1px;
                    color: #94a3b8;
                    margin-bottom: 20px;
                }
                .timeline-container {
                    display: flex;
                    flex-direction: column;
                    gap: 0px;
                }
                .timeline-item {
                    display: flex;
                    gap: 20px;
                    padding-bottom: 30px;
                    position: relative;
                }
                .timeline-item::before {
                    content: '';
                    position: absolute;
                    left: 6px;
                    top: 15px;
                    bottom: 0;
                    width: 2px;
                    background: #e2e8f0;
                }
                .timeline-item:last-child::before {
                    display: none;
                }
                .timeline-dot-creator {
                    width: 14px;
                    height: 14px;
                    background: #94a3b8;
                    border-radius: 50%;
                    z-index: 1;
                    margin-top: 5px;
                }
                .timeline-dot-staff {
                    width: 14px;
                    height: 14px;
                    background: #6366f1;
                    border-radius: 50%;
                    z-index: 1;
                    margin-top: 5px;
                    box-shadow: 0 0 0 4px #eef2ff;
                }
                .timeline-content {
                    flex: 1;
                    padding: 12px 16px;
                    border-radius: 12px;
                    border: 1px solid #f1f5f9;
                }
                .staff-bg {
                    background-color: #f8faff;
                    border-left: 4px solid #6366f1;
                }

                .creator-highlight-bg {
                    background-color: #f1f5f9; /* Soft Slate background */
                    border: 1px solid #e2e8f0 !react;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                }

                .creator-badge {
                    background: #475569;
                    color: white;
                    padding: 2px 8px;
                    border-radius: 4px;
                    font-size: 10px;
                    text-transform: uppercase;
                    font-weight: 700;
                    letter-spacing: 0.5px;
                }

                .staff-bg {
                    background-color: #f8faff;
                    border-left: 4px solid #6366f1;
                    margin-left: 10px; /* Indent staff replies slightly */
                }
            `}</style>
        </div>
    );
};

const InfoItem = ({ icon, label, value, highlightPriority, highlightStatus }) => (
    <div style={{ marginBottom: '22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '13px', marginBottom: '6px' }}>
            {React.cloneElement(icon, { size: 14 })} <span>{label}</span>
        </div>
        
        {highlightPriority ? (
            <div className={`modern-priority priority-${highlightPriority}`} style={{ width: 'fit-content' }}>
                <span className="dot"></span> {value}
            </div>
        ) : highlightStatus ? (
            <div className={`modern-badge status-${highlightStatus}`} style={{ width: 'fit-content' }}>
                {value}
            </div>
        ) : (
            <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '15px' }}>{value || "—"}</div>
        )}
    </div>
);

export default TicketDetail;